import { useCallback } from 'react';

import { InfiniteData } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { PaginatedResponse } from '@/modules/common';
import { GroupBy } from '@/modules/common/types';

import { getAssignmentsByProject } from '../../api/assignments-by-project.api';
import {
  buildAssignmentFingerprint,
  buildStateChangeFingerprint,
  buildTimeOffFingerprint,
} from '../../helpers/assignment-cache-utils';
import {
  backfillCrossGroupMemberData,
  deepCopyMember,
  mergeAssignments,
  mergeStateChanges,
  mergeTimeOffs,
  mergeUtilizationByDate,
  PageResponseWithMetadata,
} from '../../helpers/planner-base/planner-pagination-helpers';
import { PlannerProjectResponse } from '../../types/dto';
import { useAssignmentsBase } from './use-assignments-base';

interface UseAssignmentsByProjectParams {
  filters: FilterValues['values'];
  enabled?: boolean;
}

/**
 * Build a content-level fingerprint for project pages.
 * Must change when mutations patch the cache so select() bypasses its memoization.
 */
function buildProjectFingerprint(
  data: InfiniteData<PageResponseWithMetadata<PlannerProjectResponse>>
): string {
  return data.pages
    .map(p => {
      const projects = p.items as PlannerProjectResponse[];
      const assignmentFingerprint = projects
        .flatMap(proj => proj.members.flatMap(m => m.assignments.map(buildAssignmentFingerprint)))
        .join(',');
      const timeOffFingerprint = projects
        .flatMap(proj => proj.members.flatMap(m => m.timeOffs.map(buildTimeOffFingerprint)))
        .join(',');
      const utilizationFingerprint = projects
        .flatMap(proj =>
          proj.members.map(m => m.utilizationByDate.reduce((sum, u) => sum + u.assigned, 0))
        )
        .join(',');
      // Group-level utilization MUST be in the fingerprint too. ignorePlaceholdersForGroup
      // changes only the project's aggregate utilizationByDate (placeholders excluded), not
      // member-level data — so without this the select() memo stays stale and the group row
      // doesn't update until a full reload.
      const groupUtilizationFingerprint = projects
        .map(proj => proj.utilizationByDate.reduce((sum, u) => sum + u.assigned + u.capacity, 0))
        .join(',');
      // Member-info column fields (location, department, capacity, rates, …) live on the
      // state changes, not on assignments — include them so editing them re-runs select().
      const stateChangeFingerprint = projects
        .flatMap(proj =>
          proj.members.flatMap(m => m.stateChanges?.map(buildStateChangeFingerprint) ?? [])
        )
        .join(',');
      return `${p.items.length}:${p.pagination?.cursor}:${p._dateRange.startDate}:${assignmentFingerprint}:${timeOffFingerprint}:${utilizationFingerprint}:${groupUtilizationFingerprint}:${stateChangeFingerprint}`;
    })
    .join('|');
}

/**
 * Merges project data across all loaded pages (vertical + horizontal pagination).
 *
 * TODO: Currently merges ALL users from ALL loaded date ranges within each project.
 * Users who only have data in a specific date range still appear when scrolling
 * to other periods. Ideally, visibility should be filtered by the current viewport.
 * Related: Same issue exists in use-assignments-by-member.ts and use-assignments-by-department.ts
 */
function selectProjectPages(
  data: InfiniteData<PageResponseWithMetadata<PlannerProjectResponse>>
): PaginatedResponse<PlannerProjectResponse> {
  // Combine all loaded pages to show the full visible date range
  // Filter out items with null project (backend may return unassigned members without a project)
  const allItems = data.pages.flatMap(page => page.items.filter(item => item.project != null));

  // Merge assignments for the same project across different date ranges and cursor pages
  const mergedProjectsMap = new Map<number, PlannerProjectResponse>();
  const projectMembersMap = new Map<
    number,
    Map<number, PlannerProjectResponse['members'][number]>
  >();

  allItems.forEach(plannerProjectResponse => {
    const projectId = plannerProjectResponse.project.id;
    const existingProject = mergedProjectsMap.get(projectId);

    if (existingProject) {
      const membersById = projectMembersMap.get(projectId);
      if (!membersById) return;

      // Merge members from different date ranges for the same project
      plannerProjectResponse.members.forEach(newMember => {
        const existingMember = membersById.get(newMember.user.id);

        if (existingMember) {
          // Merge assignments and their activePeriods across date ranges
          mergeAssignments(existingMember.assignments, newMember.assignments);

          // Merge time-off requests across date ranges
          mergeTimeOffs(existingMember.timeOffs, newMember.timeOffs);

          // Merge state changes across date ranges
          existingMember.stateChanges = mergeStateChanges(
            existingMember.stateChanges,
            newMember.stateChanges
          );

          // Merge utilization by date, avoiding duplicates
          existingMember.utilizationByDate = mergeUtilizationByDate(
            existingMember.utilizationByDate,
            newMember.utilizationByDate
          );
        } else {
          // Add new member to the project — deep copy mutable arrays
          const newMemberCopy = deepCopyMember(newMember);
          existingProject.members.push(newMemberCopy);
          membersById.set(newMemberCopy.user.id, newMemberCopy);
        }
      });

      // Merge project-level utilization by date, avoiding duplicates
      existingProject.utilizationByDate = mergeUtilizationByDate(
        existingProject.utilizationByDate,
        plannerProjectResponse.utilizationByDate
      );
    } else {
      // Deep copy members and their mutable arrays to prevent select() from
      // mutating TanStack Query's cached data across re-runs.
      // Deduplicate members by user.id first — the backend may return the same
      // member multiple times within a single project item (e.g., when a user has
      // multiple assignments to the same project across cursor boundaries).
      const membersById = new Map<number, PlannerProjectResponse['members'][number]>();
      plannerProjectResponse.members.forEach(member => {
        const existing = membersById.get(member.user.id);
        if (existing) {
          // Merge into existing deep-copied member
          mergeAssignments(existing.assignments, member.assignments);
          mergeTimeOffs(existing.timeOffs, member.timeOffs);
          existing.stateChanges = mergeStateChanges(existing.stateChanges, member.stateChanges);
          existing.utilizationByDate = mergeUtilizationByDate(
            existing.utilizationByDate,
            member.utilizationByDate
          );
        } else {
          membersById.set(member.user.id, deepCopyMember(member));
        }
      });

      const projectCopy = {
        ...plannerProjectResponse,
        utilizationByDate: [...plannerProjectResponse.utilizationByDate],
        members: Array.from(membersById.values()),
      };

      mergedProjectsMap.set(projectId, projectCopy);
      projectMembersMap.set(projectId, membersById);
    }
  });

  // Backfill missing data for users that appear in multiple projects
  // (e.g., after horizontal pagination when user changed projects)
  backfillCrossGroupMemberData(projectMembersMap);

  // Get total from first page (represents total projects, not affected by date range)
  const total = data.pages[0]?.pagination?.total ?? 0;

  // Don't sort — preserve insertion order from the backend so that new
  // vertical pages simply append rows at the bottom without reordering
  // existing rows (which causes visible UI jumps).
  return new PaginatedResponse<PlannerProjectResponse>({
    items: Array.from(mergedProjectsMap.values()),
    pagination: {
      total,
      cursor: data.pages[data.pages.length - 1]?.pagination?.cursor ?? null,
    },
  });
}

/**
 * Hook for fetching planner assignments grouped by project.
 * Supports dual-axis infinite scroll:
 * - Horizontal: Load past/future date ranges
 * - Vertical: Load more projects via cursor pagination
 */
export function useAssignmentsByProject({
  filters,
  enabled = true,
}: UseAssignmentsByProjectParams) {
  // For project grouping, the backend's pageSize is member-weighted:
  // a project with 5 members counts as 5 towards the page size limit.
  // So we weight each item by its member count for dynamic page size calculation.
  const getItemWeight = useCallback(
    (item: PlannerProjectResponse) => item.members?.length ?? 1,
    []
  );

  return useAssignmentsBase<PlannerProjectResponse>(
    { filters, enabled },
    {
      groupByKey: GroupBy.PROJECTS,
      queryKeyPrefix: 'assignments-by-project',
      logTag: 'useAssignmentsByProject',
      apiFn: getAssignmentsByProject,
      selectFn: selectProjectPages,
      buildFingerprint: buildProjectFingerprint,
      getItemWeight,
    }
  );
}
